<!--Anidesk player-->

<script>
    import {
        Original,
        ModeA,
        render,
        ModeBB,
        ModeB,
        DoG,
        BilateralMean,
        CNNM,
        CNNSoftM,
        CNNSoftVL,
        CNNVL,
        CNNUL,
        CNNx2M,
        CNNx2VL,
        DenoiseCNNx2VL,
        GANx3L,
        GANx4UUL,
        GANUUL,
        ModeAA,
        ModeCA,
        CNNx2UL,
        ModeC,
    } from "anime4k-webgpu";
    import { localStorageWritable } from "@babichjacob/svelte-localstorage";
    import PlayerGui from "../components/player/PlayerGUI.svelte";
    import { onMount, onDestroy, tick } from "svelte";
    import {
        AniLibriaParser,
        SibnetParser,
        KodikParser,
    } from "../utils/LinkParser";
    import utils from "../utils";
    import { push } from "svelte-spa-router";

    export let params = {};
    let args = null;

    const upscaleModeMap = {
        0: DoG,
        1: BilateralMean,
        2: CNNM,
        3: CNNSoftM,
        4: CNNSoftVL,
        5: CNNVL,
        6: CNNUL,
        7: GANUUL,
        8: CNNx2M,
        9: CNNx2VL,
        10: DenoiseCNNx2VL,
        11: CNNx2UL,
        12: GANx3L,
        13: GANx4UUL,
        14: ModeA,
        15: ModeB,
        16: ModeC,
        17: ModeAA,
        18: ModeBB,
        19: ModeCA,
    };

    let currentTime,
        durationTime,
        upscaleSettings,
        playerSettings,
        playingSettings,
        volPercent;

    let defaultCanvasSize = {
        width: screen.width,
        height: screen.height,
    };

    let video,
        canvas,
        timePos,
        volControl,
        timeout,
        mainDiv,
        hls,
        currentEpisode,
        startTimestamp;

    let progressPercent, loadedPercent;

    let isHidden, isPaused, isTimePosClick, isFullscreen;
    let pressedKeys = new Set();

    const playerSettingsRaw = localStorageWritable(
        "playerSettings",
        utils.playerDefaultSettings,
    );

    playerSettingsRaw.subscribe((value) => {
        playerSettings = value;
    });

    const playingSettingsRaw = localStorageWritable(
        "playingSettings",
        utils.playingDefaultSettings,
    );

    playingSettingsRaw.subscribe((value) => {
        playingSettings = value;
    });

    const upscaleSettingsRaw = localStorageWritable(
        "upscaleSettings",
        utils.upscaleDefaultSettings,
    );

    upscaleSettingsRaw.subscribe((value) => {
        upscaleSettings = value;
    });

    let upscaleEnabled = upscaleSettings.enabled;

    async function changeUpscale(enabled) {
        upscaleEnabled = enabled;
        await renderUpscale();
    }

    //aspect-16-9
    //aspect-4-3
    //aspect-fit
    let aspectRatio = `aspect-${playerSettings.defaultAspectRatio}`;

    function changeAspectRatio(aspect) {
        playerSettings.defaultAspectRatio = aspect;
        aspectRatio = `aspect-${aspect}`;
    }

    let loading = true;

    function hideOnIdle() {
        if (timeout) {
            clearTimeout(timeout);
        }

        timeout = setTimeout(() => {
            if (!isHidden) {
                isHidden = true;
            }
        }, playerSettings.timeHideInterface);

        if (isHidden) {
            isHidden = false;
        }
    }

    function handleResize(event) {
        isFullscreen = window.innerHeight === screen.height;
    }

    document.addEventListener("mousemove", hideOnIdle);

    window.addEventListener("resize", handleResize);

    // Initial Load Logic
    $: if (
        params.releaseId &&
        params.translationId &&
        params.sourceId &&
        params.episodeId &&
        (!args || args.release.id != params.releaseId)
    ) {
        loadData();
    }

    async function loadData() {
        loading = true;
        try {
            const release = await anixApi.release.info(
                Number(params.releaseId),
            );
            const { episodes } = await anixApi.release.getEpisodes(
                Number(params.releaseId),
                Number(params.translationId),
                Number(params.sourceId),
            );
            const currentEpisodeData = episodes.find(
                (e) =>
                    e.id == params.episodeId || e.position == params.episodeId,
            ); // Handle ID or position

            if (!currentEpisodeData) throw new Error("Episode not found");

            currentEpisode = currentEpisodeData;

            // Resolve links
            const { avaliableQuality, src } =
                await getStreamLinks(currentEpisode);

            if (!src) {
                console.warn("No stream link found for episode");
            }

            args = {
                release: release.release, // API info() returns {code, release}, verify this
                episodes: episodes,
                currentEpisode: currentEpisodeData,
                avaliableQuality: avaliableQuality,
                src: src || "", // Fallback to empty string avoiding null
            };

            await tick();

            if (video && args.src) {
                playVideo(currentEpisode);
            }
        } catch (e) {
            console.error("Failed to load player data:", e);
            // Handle error (redirect back or show error)
        }
        loading = false;
    }

    onMount(async () => {
        // Wait for args to be populated before init
        const checkArgs = setInterval(() => {
            if (args) {
                clearInterval(checkArgs);
                init();
            }
        }, 100);
    });

    async function getStreamLinks(episode) {
        let avaliableQuality, link;

        let source;
        // In existing code, sometimes episode.source is number.
        if (typeof episode.source == "number") {
            // We can only find source if we have episodes list.
            // If we are calling this from loadData, we have 'episodes' local var if passing it.
            // But simpler: assumes we have args.episodes or use the list we just fetched (if passed).
            // Let's rely on args if available, or pass episodes.
            source = args?.episodes?.find(
                (x) => episode.source == x.source["@id"],
            )?.source;
            // If args is null (during loadData), we need to fix this.
            // But wait, the source structure from API usually contains full object?
            // If not, we need the list.
        } else {
            source = episode.source;
        }

        // If source missing (e.g. args not set yet), we need a fallback.
        // During loadData, episode comes from 'episodes' array from API, effectively containing proper source object usually?
        if (!source && !args) {
            // We assume episode object from API is complete enough or we cant resolve.
            // But existing code does look up by ID.
            // We will handle this by making sure `currentEpisode` passed here is from the list.
            // If episode.source is number, we need the list.
        }

        // Refined getStreamLinks needs episodes list if source is ID
        // But let's assume valid episode object for now.

        // Actually, let's duplicate logic for now, using a helper function is risky if dependencies aren't clear.
        // I will keep logic inside functions but reuse code.

        // ... (Reusing logic from playVideo) ...
        const sourceName = source?.name || "Unknown";
        switch (sourceName) {
            case "Kodik":
                let aQ = {};
                const kLinks = await KodikParser.getDirectLinks(episode.url);
                for (const [key, value] of Object.entries(kLinks)) {
                    aQ[key] = {
                        src: value[0].src,
                    };
                }
                avaliableQuality = aQ;
                break;

            case "Liberty":
            case "Libria":
                await utils.fallback(async (success) => {
                    const aLinks = await AniLibriaParser.getDirectLinks(
                        episode.url,
                    );
                    avaliableQuality = aLinks;

                    success = true;
                }, 3);
                break;

            case "Sibnet":
                await utils.fallback(async (success) => {
                    const link = await SibnetParser.Parse(episode.url); // Use SibnetParser (was Sibnet in original param?)
                    if (!link) return;

                    avaliableQuality = {
                        "720": {
                            src: link,
                        },
                    };

                    success = true;
                }, 3);
                break;
        }

        const url =
            avaliableQuality?.[String(playingSettings.defaultQuality)]?.src ??
            avaliableQuality?.["720"]?.src;

        return {
            avaliableQuality,
            src: url ? `${URL.canParse(url) ? url : `https:${url}`}` : null,
        };
    }

    // Replace getStreamLinks usage to correctly handle dependencies...
    // Actually, I'll inline resolving in loadData for now to ensure I have access to 'episodes' variable variables.

    async function playVideo(episode) {
        if (!video) return;
        let avaliableQuality, link;

        // Resolve source
        let source = episode.source;
        if (typeof source == "number") {
            source = args.episodes.find(
                (x) => episode.source == x.source["@id"],
            )?.source;
        }

        if (!source) return;

        switch (source.name) {
            case "Kodik":
                let aQ = {};
                const kLinks = await KodikParser.getDirectLinks(episode.url);
                for (const [key, value] of Object.entries(kLinks)) {
                    aQ[key] = {
                        src: value[0].src,
                    };
                }
                avaliableQuality = aQ;
                break;

            case "Liberty":
            case "Libria":
                await utils.fallback(async (success) => {
                    const aLinks = await AniLibriaParser.getDirectLinks(
                        episode.url,
                    );
                    avaliableQuality = aLinks;

                    success = true;
                }, 3);
                break;

            case "Sibnet":
                await utils.fallback(async (success) => {
                    const link = await SibnetParser.Parse(episode.url);
                    if (!link) return;

                    avaliableQuality = {
                        "720": {
                            src: link,
                        },
                    };

                    success = true;
                }, 3);
                break;
        }

        const url =
            avaliableQuality?.[String(playingSettings.defaultQuality)]?.src ??
            avaliableQuality?.["720"]?.src;

        args.avaliableQuality = avaliableQuality;

        link = `${URL.canParse(url) ? url : `https:${url}`}`;

        if (Hls.isSupported() && !new URL(link).pathname.endsWith(".mp4")) {
            if (hls) {
                hls.detachMedia();
                hls.destroy();
            }

            hls = new Hls();

            hls.on(Hls.Events.BUFFER_APPENDING, (e, data) => {
                loadedPercent =
                    (data.frag._streams.video.endPTS / video.duration) * 100;
            });

            hls.loadSource(link);
            hls.attachMedia(video);
        } else {
            video.src = link;
        }

        args.src = link;

        await video.play();

        if (!playingSettings.disableHistory) {
            // Use param IDs or resolved object IDs
            anixApi.release.markEpisodeAsWatched(
                args.release.id,
                source.id,
                currentEpisode.position,
            );
            anixApi.release.addToHistory(
                args.release.id,
                source.id,
                currentEpisode.position,
            );
        }

        startTimestamp = Date.now();
    }

    async function renderUpscale() {
        canvas = await waitForElm(".player-canvas");
        await render({
            video,
            canvas,
            pipelineBuilder: (device, inputTexture) => {
                const nativeDimensions = {
                    width: video.videoWidth,
                    height: video.videoHeight,
                };

                const targetDimensions = {
                    width: defaultCanvasSize.width,
                    height: defaultCanvasSize.height,
                };

                return [
                    upscaleEnabled
                        ? new upscaleModeMap[upscaleSettings.mode]({
                              device,
                              inputTexture,
                              nativeDimensions,
                              targetDimensions,
                          })
                        : new Original({
                              device,
                              inputTexture,
                              nativeDimensions,
                              targetDimensions,
                          }),
                ];
            },
        });
    }

    async function changeQuality(quality) {
        const qualitySrc = args.avaliableQuality[String(quality)]?.src;
        if (!qualitySrc) return;

        const url = URL.canParse(qualitySrc)
            ? qualitySrc
            : `https:${qualitySrc}`;

        if (Hls.isSupported()) {
            const currentTime = video.currentTime;
            const isPausedNow = video.paused;

            hls.detachMedia();
            hls.destroy();

            hls = new Hls();

            hls.on(Hls.Events.BUFFER_APPENDING, (e, data) => {
                loadedPercent =
                    (data.frag._streams.video.endPTS / video.duration) * 100;
            });

            hls.loadSource(url);
            hls.attachMedia(video);

            hls.on(Hls.Events.MANIFEST_PARSED, () => {
                video.currentTime = currentTime;
                if (!isPausedNow) {
                    video.play();
                }
            });
        } else {
            video.src = url;
            video.play();
        }

        if (avaliableGPU) await renderUpscale();

        args.src = url;
        args.currentQuality = quality;
    }

    async function init() {
        mainDiv = await waitForElm(".anidesk-player");
        video = await waitForElm(".player-video");

        if (!args.src) {
            console.warn("Invalid source in init");
            return;
        }

        if (
            Hls.isSupported() &&
            args.src &&
            !new URL(args.src).pathname.endsWith(".mp4")
        ) {
            hls = new Hls();

            hls.on(Hls.Events.BUFFER_APPENDING, (e, data) => {
                loadedPercent =
                    (data.frag._streams.video.endPTS / video.duration) * 100;
            });

            hls.loadSource(args.src);
            hls.attachMedia(video);
        } else {
            video.src = args.src;
        }

        video.volume = playerSettings.saveUserVolume.enabled
            ? (playerSettings.saveUserVolume.lastValue ??
              playerSettings.defaultVolume / 100)
            : playerSettings.defaultVolume / 100;

        volControl = await waitForElm("#volume-position");

        volControl.value = video.volume;
        volPercent = video.volume * 100;

        volControl.oninput = () => {
            video.volume = volControl.value;

            if (playerSettings.saveUserVolume.enabled) {
                playerSettings.saveUserVolume.lastValue = volControl.value;
                playerSettingsRaw.set(playerSettings);
            }
        };

        video.onloadedmetadata = () => {
            loading = true;
            durationTime = utils.returnFormatedTime(video.duration);
        };

        video.onwaiting = () => {
            loading = true;
        };

        video.onplaying = () => {
            loading = false;
        };

        if (avaliableGPU) await renderUpscale();
        await video.play();

        window.onwheel = (e) => {
            switch (true) {
                case e.deltaY > 0 && volControl.value > 0:
                    volControl.value = volControl.value - 0.05;
                    video.volume -= 0.05;
                    volPercent = video.volume * 100;
                    break;

                case e.deltaY < 0 && volControl.value < 1:
                    volControl.value = parseFloat(volControl.value) + 0.05;
                    video.volume += 0.05;
                    volPercent = video.volume * 100;
                    break;
            }
        };

        window.onkeydown = (e) => {
            pressedKeys.add(e.code);

            for (const [action, keys] of Object.entries(
                playerSettings.hotkeys,
            )) {
                if (keys.length !== pressedKeys.size) continue;

                if (keys.every((key) => pressedKeys.has(key))) {
                    switch (action) {
                        case "hotkeyPlayPause":
                            isPaused ? video.play() : video.pause();
                            break;

                        case "hotkeyMute":
                            video.muted = !video.muted;
                            break;

                        case "hotkeyFullscreen":
                            isFullscreen
                                ? elecWindow.exitFullscreen()
                                : elecWindow.enterFullscreen();
                            break;

                        case "hotkeyForward":
                            video.currentTime += 10;
                            break;

                        case "hotkeyBackward":
                            video.currentTime -= 10;
                            break;

                        case "hotkeySkipOpening":
                            video.currentTime += 85;
                            break;

                        case "hotkeyNextEpisode":
                            let e =
                                args.episodes[
                                    args.episodes.findIndex(
                                        (x) =>
                                            x.position ==
                                            currentEpisode.position,
                                    ) + 1
                                ];

                            if (e) {
                                currentEpisode = e;
                                playVideo(currentEpisode);
                            }
                            break;

                        case "hotkeyPrevEpisode":
                            let p =
                                args.episodes[
                                    args.episodes.findIndex(
                                        (x) =>
                                            x.position ==
                                            currentEpisode.position,
                                    ) - 1
                                ];

                            if (p) {
                                currentEpisode = p;
                                playVideo(currentEpisode);
                            }
                            break;
                    }

                    pressedKeys.clear();
                    break;
                }
            }

            switch (e.code) {
                case "Escape":
                    if (isFullscreen) {
                        elecWindow.exitFullscreen();
                    }
                    push(`/release/${args.release.id}`);
                    break;
            }
        };

        window.onkeyup = (e) => {
            pressedKeys.delete(e.code);
        };

        window.onblur = () => {
            pressedKeys.clear();
        };

        durationTime = utils.returnFormatedTime(video.duration);
        startTimestamp = Date.now();

        video.onpause = () => {
            isPaused = true;
            loading = false;
        };

        video.onplay = (e) => {
            isPaused = false;
            loading = false;
            startTimestamp = Date.now();
        };

        video.onended = async () => {
            if (playerSettings.autoplayEpisode) {
                let e = args.episodes.find(
                    (x) => x.position == currentEpisode.position + 1,
                );

                if (e) {
                    currentEpisode = e;
                    await playVideo(currentEpisode);
                }
            }
        };

        video.ontimeupdate = () => {
            currentTime = utils.returnFormatedTime(video.currentTime);
            progressPercent = (video.currentTime / video.duration) * 100;
        };

        // Note: args.currentEpisode might be stale if we relied on arg injection, but we updated args.currentEpisode in loadData.
    }

    onDestroy(() => {
        if (hls) {
            hls.detachMedia();
            hls.destroy();
        }
        document.removeEventListener("mousemove", hideOnIdle);
        window.removeEventListener("resize", handleResize);
        window.onwheel = null;
        window.onkeydown = null;
        window.onkeyup = null;
        window.onblur = null;
        if (video) {
            video.onpause = null;
            video.onplay = null;
            video.ontimeupdate = null;
            video.onloadedmetadata = null;
            video.onwaiting = null;
            video.onplaying = null;
        }
        video = null;

        if (volControl) volControl.oninput = null;
        clearTimeout(timeout);
    });
</script>

{#if args}
    <div class="anidesk-player full">
        <PlayerGui
            {playVideo}
            {args}
            {isHidden}
            {isFullscreen}
            {isPaused}
            {video}
            {isTimePosClick}
            {timePos}
            {progressPercent}
            {loadedPercent}
            {volControl}
            {canvas}
            {mainDiv}
            {currentTime}
            {durationTime}
            bind:cEpisode={currentEpisode}
            transparentPercent={playerSettings.opacityInterface}
            {changeQuality}
            {changeUpscale}
            {upscaleEnabled}
            {changeAspectRatio}
            aspectRatio={utils.aspectRatioValues.find(
                (x) => x.value == playerSettings.defaultAspectRatio,
            ).label}
            bind:volumePercent={volPercent}
        />

        <span class:hide={!loading} class="loader"></span>

        {#if avaliableGPU}
            <canvas
                class="player-canvas {aspectRatio}"
                width={screen.width}
                height={screen.height}
            ></canvas>
        {/if}
        <video
            class="player-video"
            crossorigin="anonymous"
            class:full={!avaliableGPU}
            class:hide={avaliableGPU}
        ></video>
    </div>
{:else}
    <div>Loading...</div>
{/if}

<style>
    .full {
        width: 100%;
        height: 100%;
        overflow: hidden;
    }

    .loader {
        position: absolute;
        left: 50%;
        top: 50%;
        transform: translate(-50%, -50%);
        width: 30px;
        height: 30px;
        border: 3px solid var(--player-timeline-progress-color);
        border-bottom-color: transparent;
        border-radius: 50%;
        display: inline-block;
        box-sizing: border-box;
        animation: rotation 1s linear infinite;
        z-index: 1;
    }

    @keyframes rotation {
        0% {
            transform: translate(-50%, -50%) rotate(0deg);
        }
        100% {
            transform: translate(-50%, -50%) rotate(360deg);
        }
    }

    canvas,
    video {
        cursor: none;
    }

    .player-canvas {
        height: 100%;
        overflow: hidden;
        position: absolute;
        left: 50%;
        transform: translateX(-50%);
    }

    .aspect-16-9 {
        aspect-ratio: 16 / 9;
    }

    .aspect-4-3 {
        aspect-ratio: 4 / 3;
    }

    .aspect-fit {
        width: 100%;
    }

    .anidesk-player {
        background-color: black;
    }
</style>
